<template>
    <div
        class="chart-container"
        :class="{ 'chart-container--tooltip-pin': canPinTooltip }"
    >
        <RedesignBaseSpinner
            v-if="loading"
            class="absolute right-1 top-1"
        />

        <slot name="pre" />

        <VChart
            v-if="!hide"
            ref="chartRef"
            :class="`chart ${classes}`"
            :option="normalizedOption"
            autoresize
            @legendselectchanged="onLegendChange"
        />

        <slot name="post" />
    </div>
</template>

<script lang="ts" setup>
import { provide, ref, computed } from 'vue'
import VChart, { THEME_KEY } from 'vue-echarts'
import type { ComponentExposed } from 'vue-component-type-helpers'

import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'

import useChartActions from '@/composables/useChartActions'
import {
    hasTooltipFormatterFunction,
    prepareChartTooltipPinOptions,
    useChartTooltipPin,
} from '@/composables/useChartTooltipPin'
import { activeLayoutColors } from '@/composables/globalData/useLayouts'

import { WidgetExtended } from '@/types/dashboard'
import { ChartOptions } from '@/types/echart'
import type { LegendComponentOption } from 'echarts/components'

/* Props */
const props = withDefaults(
    defineProps<{
        widget: WidgetExtended
        classes?: string
        hide?: boolean
        loading?: boolean
        tooltipPin?: boolean
        option: ChartOptions // TODO after migration to vue 3 do component generic that will extend ECBasicOption
    }>(),
    {
        classes: '',
        hide: false,
        loading: false,
        tooltipPin: false
    }
)

/* Data */
const chartRef = ref<ComponentExposed<typeof VChart>>()
const emit = defineEmits<{
    (event: 'on-update-legend', payload: LegendComponentOption): void
}>()

function mapOptionItems (items: any, mapItem: (item: any) => any) {
    const list = [ items ].flat().map(item => item ? mapItem(item) : item)
    return Array.isArray(items) ? list : list[0]
}

function withLayoutTextColor (option: ChartOptions): ChartOptions {
    const color = activeLayoutColors.value.mainTextColor
    const source: any = option

    return {
        ...source,
        textStyle: { ...source.textStyle, color },
        legend: mapOptionItems(source.legend, (legend) => ({
            ...legend,
            textStyle: { ...legend.textStyle, color },
        })),
        xAxis: mapOptionItems(source.xAxis, (axis) => ({
            ...axis,
            axisLabel: { ...axis.axisLabel, color },
            nameTextStyle: { ...axis.nameTextStyle, color },
        })),
        yAxis: mapOptionItems(source.yAxis, (axis) => ({
            ...axis,
            axisLabel: { ...axis.axisLabel, color },
            nameTextStyle: { ...axis.nameTextStyle, color },
        })),
    }
}

/* Computed */
const echartsInstanceRef = computed(() => chartRef.value?.chart)
const canPinTooltip = computed(() => !!props.tooltipPin && hasTooltipFormatterFunction(props.option))
const normalizedOption = computed(() => {
    if (!props.option || typeof props.option !== 'object') {
        return props.option
    }
    
    const optionWithLayoutTextColor = withLayoutTextColor(props.option)
    if (!canPinTooltip.value) {
        return optionWithLayoutTextColor
    }
    return prepareChartTooltipPinOptions(optionWithLayoutTextColor)
})

/* Composable */
useChartActions(props.widget, chartRef)

useChartTooltipPin(
    echartsInstanceRef,
    canPinTooltip
)

/* Provide */
provide(THEME_KEY, 'light')

/* Methods */
function updateSeries (series: Array<object>, notMerge = false, lazyUpdate = false) {
    chartRef.value?.setOption(
        {
            series
        },
        notMerge,
        lazyUpdate
    )
}
function resizeChart () {
    chartRef.value?.resize()
}

function getChartWidth () {
    return chartRef.value?.getWidth()
}
function onLegendChange (params: LegendComponentOption) {
    emit('on-update-legend', params)
}

/* Expose */
defineExpose({
    updateSeries,
    resizeChart,
    getChartWidth
})

</script>

<style lang="scss">
.chart-container {
    @apply w-full relative;

    .chart {
        @apply w-full;
    }
}

.dashboard-chart-tooltip-pin-host {
    user-select: text !important;
    -webkit-user-select: text;
    -moz-user-select: text;

    .dashboard-tooltip-pin-icon {
        color: var(--accent-color);
    }

    .dashboard-tooltip-pin-icon + .dashboard-tooltip-pin-icon + * {
        display: inline-block;
        vertical-align: middle;
    }

    .dashboard-tooltip-pin-icon--pinned {
        display: none;
    }

    &.dashboard-chart-tooltip-pin-host--pinned {
        .dashboard-tooltip-pin-icon--idle {
            display: none;
        }

        .dashboard-tooltip-pin-icon--pinned {
            display: inline-block;
        }
    }
}
</style>
